import React, { useCallback, useEffect, useRef, useState } from 'react';
import logo from '../../assets/images/ic_launcher.png';
import './HeaderComponent.css';
import { useAuth0 } from '../../auth/useAuth0';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell,
    faMoon,
    faSun,
    faUser,
    faCalendarPlus,
    faCalendarCheck,
    faCalendarXmark,
    faBan,
    faMoneyBillWave,
    faUserClock,
    faWandMagicSparkles,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useUser } from "../../auth/UserContext";
import { useBusinessContext } from "../../context/BusinessContext";
import { usePermissions } from "../../context/PermissionsContext";
import { usePlan } from '../../context/PlanContext';
import PlanCrown from '../plan/PlanCrown';
import {
    getBusinessFromLocalStorage,
    saveBusinessToLocalStorage
} from "../../util/local-storage-service";
import { useNavigation } from "../../context/NavigationContext";
import AiAssistantPanel from './AiAssistantPanel';
import { MENU, notificationTargetMenu } from "../util/Utils";
import { useOpenNotification } from "../../hooks/useOpenNotification";
import { mediaUrl } from "../../service/media/media-service";
import { Notification } from "../../model/notification/Notification";
import {
    getNotifications,
    getUnreadNotificationCount,
    markAllNotificationsRead,
} from "../../service/notification/notification-service";
import { Appointment } from "../../model/appointment/Appointment";
import {
    getMyWaitingRoom,
    getMyExpectedToday,
    callPatient,
    checkInAppointment,
} from "../../service/appointment/appointment-service";
import { findBusinessSettingByBusinessId } from "../../service/business/business-service";
import { playArrivalSound, playCalledSound } from "../../util/sounds";
import { useAlert } from "../../context/alert/AlertContext";

/** How often to poll the unread-count badge (ms). */
const POLL_INTERVAL_MS = 20_000;

/** Number of notifications to fetch when the dropdown opens. */
const NOTIF_FETCH_LIMIT = 15;

/** How often to poll the waiting room list (ms). */
const WAITING_ROOM_POLL_MS = 15_000;

/** Minutes since an ISO datetime string. Returns -1 when unparseable. */
/** How long a scheduled patient stays in "A chegar" after their time passes
 *  before being treated as a no-show and hidden (minutes). */
const WAITING_LATE_LIMIT_MIN = 60;

function minutesSince(iso?: string | null): number {
    if (!iso) return -1;
    const then = new Date(iso);
    if (isNaN(then.getTime())) return -1;
    return Math.floor((Date.now() - then.getTime()) / 60_000);
}

/** Format startTime "HH:MM" from an ISO local datetime ("2026-06-14T14:30:00"). */
function formatTime(iso?: string | null): string {
    if (!iso) return '';
    // ISO local datetime — no timezone, just slice.
    const t = iso.indexOf('T');
    if (t === -1) return iso;
    return iso.slice(t + 1, t + 6); // "HH:MM"
}

/**
 * Format an ISO createdAt string into a human-readable relative time.
 * Falls back gracefully when the value is missing or unparseable.
 * Receives the i18n `t` function so it stays locale-aware.
 */
function relativeTime(iso: string | undefined, t: (key: string, opts?: Record<string, unknown>) => string): string {
    if (!iso) return '';
    const then = new Date(iso);
    if (isNaN(then.getTime())) return '';
    const diffMs = Date.now() - then.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return t('header.time.now');
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return t('header.time.minAgo', { min: diffMin });
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return t('header.time.hAgo', { h: diffH });
    const diffD = Math.floor(diffH / 24);
    if (diffD === 1) return t('header.time.yesterday');
    if (diffD < 7) return t('header.time.daysAgo', { days: diffD });
    return then.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });
}

/**
 * Resolve a CSS color token for the small category dot.
 * The dot already uses `.gs-notif__dot` — we just tint its background for
 * known categories to give a quick visual cue.
 */
/** Category → { icon, color } for the notification list. */
function categoryVisual(category?: string): { icon: IconDefinition; color: string } {
    switch (category) {
        case 'APPOINTMENT_BOOKED':
            return { icon: faCalendarPlus, color: '#3b82f6' };    // blue
        case 'APPOINTMENT_CONFIRMED':
            return { icon: faCalendarCheck, color: '#22c55e' };   // green
        case 'APPOINTMENT_CANCELLED':
            return { icon: faCalendarXmark, color: '#ef4444' };   // red
        case 'APPOINTMENT_DECLINED':
            return { icon: faBan, color: '#ef4444' };             // red
        case 'PAYMENT_RECEIVED':
            return { icon: faMoneyBillWave, color: '#22c55e' };   // green
        default:
            return { icon: faBell, color: '#7c6cf0' };            // brand
    }
}

const HeaderComponent: React.FC = () => {
    const { t } = useTranslation();
    const { getAccessTokenSilently, logout } = useAuth0();
    const { user } = useUser();
    const { allBusiness, selectedBusiness, setSelectedBusiness } = useBusinessContext();
    const { activeMenu, setActiveMenu } = useNavigation();
    const openNotification = useOpenNotification();
    const { setAlert } = useAlert();

    // Restore the saved theme (default light). Applied to <html> via the effect below.
    const [darkMode, setDarkMode] = useState<'light' | 'dark'>(
        () => (typeof window !== 'undefined' && localStorage.getItem('gs-theme') === 'dark' ? 'dark' : 'light')
    );
    const [showNotifications, setShowNotifications] = useState(false);

    // Human-readable label of the screen behind the panel, so the assistant can
    // answer "explica essa tela" with real context (sent as a hint, not data).
    const SCREEN_LABELS: Record<string, string> = {
        [MENU.HOME]: 'Home (Sua IA)',
        [MENU.CALENDAR]: 'Calendário (agenda de atendimentos)',
        [MENU.APPOINTMENTS]: 'Agendamentos (lista de agendamentos)',
        [MENU.WAITLIST]: 'Lista de espera',
        [MENU.CLIENT]: 'Clientes',
        [MENU.PAYMENTS]: 'Pagamentos (transações e checkout)',
        [MENU.REPORTS]: 'Relatórios & analytics',
        [MENU.CHATS]: 'Mensagens (chat com clientes e equipe)',
        [MENU.SERVICES]: 'Serviços (catálogo e preços)',
        [MENU.TEAM_MEMBER]: 'Equipe (profissionais)',
        [MENU.PAYROLL]: 'Folha de Pagamento',
        [MENU.FINANCE]: 'Financeiro (DRE, lançamentos, resumo do período)',
        [MENU.SETTINGS]: 'Configurações',
        [MENU.PROFILE]: 'Perfil do usuário',
        [MENU.NOTIFICATIONS]: 'Notificações',
    };

    // Sua IA: the header button opens the chat panel (its input autofocuses);
    // the panel is scoped to the SELECTED business. The button is gated by
    // the AI_ASSISTANT permission (backend enforces it on every call anyway).
    const { canView } = usePermissions();
    const aiAllowed = canView('AI_ASSISTANT');
    // Subscription: the assistant is a PROFISSIONAL+ feature (crown + upsell otherwise).
    const { hasFeature, openUpsell } = usePlan();
    const aiInPlan = hasFeature('AI');
    const [aiPanelOpen, setAiPanelOpen] = useState(false);

    // Notification state
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [notifLoading, setNotifLoading] = useState(false);

    const notifRef = useRef<HTMLDivElement>(null);
    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Waiting-room state
    const [waitingRoomEnabled, setWaitingRoomEnabled] = useState(false);
    const [waitingList, setWaitingList] = useState<Appointment[]>([]);
    const [showWaiting, setShowWaiting] = useState(false);
    /** Which tab of the waiting-room dropdown is active. */
    const [waitingTab, setWaitingTab] = useState<'sala' | 'chegar'>('sala');
    /** Today's scheduled patients not yet arrived (the "A chegar" tab). */
    const [expectedToday, setExpectedToday] = useState<Appointment[]>([]);
    // Drop patients who are more than 1h past their scheduled time — a 13:00 slot
    // shouldn't still sit in "A chegar" at 21:00 (treat as a no-show). Recomputed
    // every render so it falls off as time passes (the list also re-polls).
    const visibleExpected = expectedToday.filter(
        (a) => minutesSince(a.startTime) <= WAITING_LATE_LIMIT_MIN,
    );
    /** Transient "X foi chamado(a)" notice shown under the chip when another
     *  session calls a patient (detected via polling). Auto-clears. */
    const [justCalled, setJustCalled] = useState<{ name: string; at?: string | null; by?: string | null } | null>(null);
    const waitingRef = useRef<HTMLDivElement>(null);
    const waitingPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const justCalledTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Waiting-patient ids from the previous poll → detect NEW arrivals by id.
    const prevWaitingIdsRef = useRef<Set<string>>(new Set());
    const waitingInitRef = useRef<boolean>(false);
    // Id of the last-called patient across polls → detect a NEW call.
    const prevCalledIdRef = useRef<string | null>(null);
    const calledInitRef = useRef<boolean>(false);
    // Ids this very session acted on (checked-in or called) — used to SILENCE
    // the sound/flash for the actor while still alerting other sessions.
    const selfActedIdsRef = useRef<Set<string>>(new Set());

    // ── Business sync (unchanged from original) ─────────────────────────────
    useEffect(() => {
        if (!allBusiness || allBusiness.length === 0) return;

        if (!selectedBusiness) {
            const cached = getBusinessFromLocalStorage();
            const cachedId = cached?.id;
            const fresh = cachedId
                ? allBusiness.find((b) => String(b.id) === String(cachedId))
                : null;
            setSelectedBusiness(fresh ?? cached ?? allBusiness[0]);
            return;
        }

        const fresh = allBusiness.find(
            (b) => String(b.id) === String(selectedBusiness.id)
        );
        if (
            fresh &&
            (fresh.brandColor !== selectedBusiness.brandColor ||
                fresh.brandColorStrong !== selectedBusiness.brandColorStrong ||
                fresh.brandColorSoft !== selectedBusiness.brandColorSoft ||
                fresh.image !== selectedBusiness.image ||
                fresh.name !== selectedBusiness.name)
        ) {
            const merged = { ...selectedBusiness, ...fresh };
            setSelectedBusiness(merged);
            saveBusinessToLocalStorage(merged);
        }
    }, [allBusiness, selectedBusiness, setSelectedBusiness]);

    // ── Unread-count polling ─────────────────────────────────────────────────
    const fetchUnreadCount = useCallback(async () => {
        if (!selectedBusiness?.id) return;
        try {
            const token = await getAccessTokenSilently();
            const count = await getUnreadNotificationCount(selectedBusiness.id, token);
            setUnreadCount(count);
        } catch {
            // Silently swallow — polling failure must never crash the header.
        }
    }, [selectedBusiness?.id, getAccessTokenSilently]);

    useEffect(() => {
        // Fetch immediately when businessId is available, then start polling.
        fetchUnreadCount();

        pollTimerRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);

        return () => {
            if (pollTimerRef.current !== null) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
    }, [fetchUnreadCount]);

    // ── Waiting-room: fetch settings once per business ──────────────────────
    useEffect(() => {
        setWaitingRoomEnabled(false);
        setWaitingList([]);
        prevWaitingIdsRef.current = new Set();
        waitingInitRef.current = false;
        prevCalledIdRef.current = null;
        calledInitRef.current = false;
        selfActedIdsRef.current = new Set();
        setExpectedToday([]);
        setJustCalled(null);

        if (!selectedBusiness?.id) return;

        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const settings = await findBusinessSettingByBusinessId(
                    Number(selectedBusiness.id),
                    token
                );
                if (!cancelled) {
                    setWaitingRoomEnabled(!!settings.enableWaitingRoom);
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[waiting-room] settings fetch failed', e);
            }
        })();

        return () => { cancelled = true; };
    }, [selectedBusiness?.id, getAccessTokenSilently]);

    // ── Waiting-room: poll whenever feature is enabled ───────────────────────
    const fetchWaitingRoom = useCallback(async () => {
        if (!selectedBusiness?.id) return;
        try {
            const token = await getAccessTokenSilently();
            const list = await getMyWaitingRoom(token, selectedBusiness.id);

            setWaitingList(list);

            // ── Arrival detection (by id) ──────────────────────────────────
            // A new waiting id appeared since the last poll. Only sound/toast if
            // it was NOT this session's own check-in (otherwise the actor would
            // hear their own action). Other sessions DO get alerted.
            const waitingIds = list.filter((a) => !a.calledAt).map((a) => a.id);
            if (waitingInitRef.current) {
                const fresh = waitingIds.filter((id) => !prevWaitingIdsRef.current.has(id));
                const external = fresh.filter((id) => !selfActedIdsRef.current.has(id));
                fresh.forEach((id) => selfActedIdsRef.current.delete(id)); // consume self
                if (external.length > 0) {
                    const newest = list
                        .filter((a) => external.includes(a.id) && !!a.arrivedAt)
                        .sort((a, b) =>
                            new Date(b.arrivedAt!).getTime() - new Date(a.arrivedAt!).getTime()
                        )[0];
                    setAlert('success', t('waitingRoom.arrivedToast', { name: newest?.customerName ?? 'Paciente' }));
                    playArrivalSound();
                }
            }
            prevWaitingIdsRef.current = new Set(waitingIds);
            waitingInitRef.current = true;

            // ── Call detection (by id) ─────────────────────────────────────
            // A new last-called patient. Silence + skip the flash if THIS session
            // made the call; alert + sound for everyone else.
            const lc = list.find((a) => !!a.calledAt) ?? null;
            const lcId = lc?.id ?? null;
            if (calledInitRef.current && lcId !== null && lcId !== prevCalledIdRef.current) {
                if (selfActedIdsRef.current.has(lcId)) {
                    selfActedIdsRef.current.delete(lcId); // own call → stay silent
                } else {
                    setJustCalled({ name: lc?.customerName ?? t('waitingRoom.waiting'), at: lc?.calledAt, by: lc?.calledBy });
                    playCalledSound();
                    if (justCalledTimerRef.current) clearTimeout(justCalledTimerRef.current);
                    justCalledTimerRef.current = setTimeout(() => setJustCalled(null), 10000);
                }
            }
            prevCalledIdRef.current = lcId;
            calledInitRef.current = true;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[waiting-room] poll failed', e);
        }
    }, [selectedBusiness?.id, getAccessTokenSilently, setAlert]);

    useEffect(() => {
        if (!waitingRoomEnabled) {
            // Clean up any running timer when the feature is disabled.
            if (waitingPollRef.current !== null) {
                clearInterval(waitingPollRef.current);
                waitingPollRef.current = null;
            }
            return;
        }

        fetchWaitingRoom();
        waitingPollRef.current = setInterval(fetchWaitingRoom, WAITING_ROOM_POLL_MS);

        return () => {
            if (waitingPollRef.current !== null) {
                clearInterval(waitingPollRef.current);
                waitingPollRef.current = null;
            }
        };
    }, [waitingRoomEnabled, fetchWaitingRoom]);

    // Clear the flash timer on unmount.
    useEffect(() => () => {
        if (justCalledTimerRef.current) clearTimeout(justCalledTimerRef.current);
    }, []);

    // Fetch today's not-yet-arrived patients (the "A chegar" tab).
    const fetchExpectedToday = useCallback(async () => {
        if (!selectedBusiness?.id) return;
        try {
            const token = await getAccessTokenSilently();
            setExpectedToday(await getMyExpectedToday(token, selectedBusiness.id));
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[waiting-room] expected-today fetch failed', e);
        }
    }, [selectedBusiness?.id, getAccessTokenSilently]);

    // Reception checks a scheduled patient in straight from the dropdown.
    const handleCheckInExpected = useCallback(async (appt: Appointment) => {
        // Optimistic: drop from the "A chegar" list immediately.
        setExpectedToday((prev) => prev.filter((a) => a.id !== appt.id));
        // This session performed the check-in → don't play the arrival sound here.
        selfActedIdsRef.current.add(appt.id);
        try {
            const token = await getAccessTokenSilently();
            await checkInAppointment(token, appt.id);
            setAlert('success', t('waitingRoom.checkedInToast', { name: appt.customerName }));
            fetchWaitingRoom();
            window.dispatchEvent(new CustomEvent('gs:waiting-room-changed', { detail: { actedId: appt.id } }));
        } catch (err) {
            setExpectedToday((prev) => [...prev, appt]);
            selfActedIdsRef.current.delete(appt.id);
            setAlert('danger', t('waitingRoom.errorCheckIn'));
        }
    }, [getAccessTokenSilently, setAlert, fetchWaitingRoom]);

    // ── Instant refresh: re-poll immediately when a check-in / call happens
    //    elsewhere (e.g. the Appointments screen dispatches this event), instead
    //    of waiting up to 15s for the next poll. The actedId (when present) marks
    //    an action performed in THIS tab so its sound/flash is suppressed here. ──
    useEffect(() => {
        if (!waitingRoomEnabled) return;
        const onChange = (e: Event) => {
            const actedId = (e as CustomEvent).detail?.actedId;
            if (actedId) selfActedIdsRef.current.add(String(actedId));
            fetchWaitingRoom();
            fetchExpectedToday();
        };
        window.addEventListener('gs:waiting-room-changed', onChange);
        return () => window.removeEventListener('gs:waiting-room-changed', onChange);
    }, [waitingRoomEnabled, fetchWaitingRoom, fetchExpectedToday]);

    // Refresh the "A chegar" list whenever the dropdown is opened.
    useEffect(() => {
        if (showWaiting && waitingRoomEnabled) fetchExpectedToday();
    }, [showWaiting, waitingRoomEnabled, fetchExpectedToday]);

    // ── Close waiting dropdown when clicking outside ─────────────────────────
    useEffect(() => {
        if (!showWaiting) return;
        const onClick = (e: MouseEvent) => {
            if (waitingRef.current && !waitingRef.current.contains(e.target as Node)) {
                setShowWaiting(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [showWaiting]);

    // ── Call patient: optimistically mark as "called" (kept visible as the
    //    last-called entry) instead of dropping it, and drop the previous
    //    last-called so only one shows. ─────────────────────────────────────────
    const handleCallPatient = useCallback(async (appt: Appointment) => {
        // Local-naive ISO (matches the backend's LocalDateTime serialization) so
        // formatTime() reads the right "HH:MM" for the optimistic "Chamado às".
        const d = new Date();
        const p = (n: number) => String(n).padStart(2, '0');
        const nowIso = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
        setWaitingList((prev) => [
            ...prev.filter((a) => a.id !== appt.id && !a.calledAt),
            { ...appt, calledAt: nowIso },
        ]);
        // This session made the call (it gets the toast below) — mark the id so
        // the follow-up poll stays silent here (no flash/sound for the caller).
        selfActedIdsRef.current.add(appt.id);
        calledInitRef.current = true;
        try {
            const token = await getAccessTokenSilently();
            await callPatient(token, appt.id);
            setAlert('success', t('waitingRoom.wasCalled', { name: appt.customerName }));
            window.dispatchEvent(new CustomEvent('gs:waiting-room-changed', { detail: { actedId: appt.id } }));
        } catch (err) {
            // Revert: put the patient back as waiting.
            setWaitingList((prev) => [
                ...prev.filter((a) => a.id !== appt.id),
                { ...appt, calledAt: null },
            ]);
            selfActedIdsRef.current.delete(appt.id);
            setAlert('danger', t('waitingRoom.errorCall'));
        }
    }, [getAccessTokenSilently, setAlert]);

    // ── Open dropdown: fetch list + mark-all-read ────────────────────────────
    const openNotifications = useCallback(async () => {
        setShowNotifications(true);

        if (!selectedBusiness?.id) return;

        setNotifLoading(true);
        try {
            const token = await getAccessTokenSilently();

            // Fetch list and fire mark-all-read in parallel.
            const [fetched] = await Promise.all([
                getNotifications(selectedBusiness.id, token, NOTIF_FETCH_LIMIT),
                markAllNotificationsRead(selectedBusiness.id, token).catch(() => {
                    // Non-fatal — best-effort mark.
                }),
            ]);

            setNotifications(fetched);
            // Optimistically clear the badge + mark items read in local state.
            setUnreadCount(0);
            setNotifications(
                fetched.map((n) => ({ ...n, read: true }))
            );
        } catch {
            // If the fetch fails just show empty list — don't crash the header.
            setNotifications([]);
        } finally {
            setNotifLoading(false);
        }
    }, [selectedBusiness?.id, getAccessTokenSilently]);

    const handleBellClick = useCallback(() => {
        if (showNotifications) {
            setShowNotifications(false);
        } else {
            openNotifications();
        }
    }, [showNotifications, openNotifications]);

    // ── Close when clicking outside ──────────────────────────────────────────
    useEffect(() => {
        if (!showNotifications) return;
        const onClick = (e: MouseEvent) => {
            if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
                setShowNotifications(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [showNotifications]);

    // ── Misc handlers (unchanged from original) ──────────────────────────────
    const logoutWithRedirect = () =>
        logout({ logoutParams: { returnTo: window.location.origin } });

    const toggleDarkMode = () =>
        setDarkMode((prev) => (prev === 'dark' ? 'light' : 'dark'));

    // Apply the theme to <html>: `data-bs-theme` drives Bootstrap 5.3 components,
    // `data-theme` drives our own --gs-* token overrides. Persist the choice.
    useEffect(() => {
        const root = document.documentElement;
        if (darkMode === 'dark') {
            root.setAttribute('data-theme', 'dark');
            root.setAttribute('data-bs-theme', 'dark');
        } else {
            root.removeAttribute('data-theme');
            root.removeAttribute('data-bs-theme');
        }
        localStorage.setItem('gs-theme', darkMode);
    }, [darkMode]);

    const handleBusinessChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selected =
            allBusiness?.find((b) => b?.id?.toString() === event.target.value) || null;
        setSelectedBusiness(selected);
        saveBusinessToLocalStorage(selected);
    };

    const isProfileActive = activeMenu === MENU.PROFILE;
    const brandColor = selectedBusiness?.brandColor || null;

    return (
        <header
            className="gs-header"
            data-cy="header-bar"
            style={
                brandColor
                    ? ({ '--gs-brand': brandColor } as React.CSSProperties)
                    : undefined
            }
        >
            {brandColor && <span className="gs-header__brand-strip" aria-hidden />}

            {/* Left: Logo + business switcher */}
            <div className="gs-header__left">
                {user ? (
                    <>
                        <span
                            className={`gs-header__logo-wrap ${
                                brandColor ? 'gs-header__logo-wrap--brand' : ''
                            }`}
                        >
                            <img
                                src={mediaUrl(selectedBusiness?.image) || logo}
                                alt="Logo"
                                className="gs-header__logo"
                            />
                        </span>
                        <select
                            value={selectedBusiness?.id || ''}
                            onChange={handleBusinessChange}
                            className={`gs-header__business-select ${
                                brandColor ? 'gs-header__business-select--brand' : ''
                            }`}
                            aria-label="Switch business"
                            data-cy="header-business-select"
                        >
                            {allBusiness?.map((business) => (
                                <option key={business.id} value={business.id}>
                                    {business.name}
                                </option>
                            ))}
                        </select>
                        {/* AI icon — opens the assistant panel (typing happens
                            there) scoped to the selected business. */}
                        {selectedBusiness && aiAllowed && (
                            <button
                                type="button"
                                className={`gs-aibtn ${aiInPlan ? '' : 'gs-aibtn--locked'}`}
                                onClick={() => (aiInPlan ? setAiPanelOpen(true) : openUpsell('AI'))}
                                aria-label={t('header.askAi')}
                                title={aiInPlan ? t('header.askAi') : t('plan.crownTitle')}
                                data-cy="header-ai-button"
                            >
                                <FontAwesomeIcon icon={faWandMagicSparkles} />
                                {!aiInPlan && <PlanCrown className="gs-aibtn__crown" />}
                            </button>
                        )}
                    </>
                ) : (
                    <h1 className="h5 mb-0">Get Service</h1>
                )}
            </div>

            {aiPanelOpen && selectedBusiness && aiAllowed && aiInPlan && (
                <AiAssistantPanel
                    businessId={Number(selectedBusiness.id)}
                    screenLabel={SCREEN_LABELS[activeMenu]}
                    onClose={() => setAiPanelOpen(false)}
                />
            )}

            {/* Right: icon actions + logout */}
            <div className="gs-header__right">
                <button
                    type="button"
                    className="gs-iconbtn"
                    onClick={toggleDarkMode}
                    aria-label={darkMode === 'dark' ? t('header.lightMode') : t('header.darkMode')}
                    title={darkMode === 'dark' ? t('header.lightMode') : t('header.darkMode')}
                >
                    <FontAwesomeIcon icon={darkMode === 'dark' ? faSun : faMoon} />
                </button>

                {/* Waiting-room chip + dropdown — the icon is always present while
                    the feature is enabled (so it's discoverable); the count badge
                    only shows when someone is actually waiting. */}
                {waitingRoomEnabled && (() => {
                    // The list carries waiting patients plus (at most) the last
                    // called one. Split them: number the waiting queue, show the
                    // last-called separately with the time it was called.
                    const waitingOnly = waitingList.filter((a) => !a.calledAt);
                    const lastCalled = waitingList.find((a) => !!a.calledAt) ?? null;
                    return (
                    <div className="gs-waiting" ref={waitingRef}>
                        <button
                            type="button"
                            className="gs-iconbtn gs-waiting__btn"
                            onClick={() => setShowWaiting((v) => !v)}
                            aria-label={t('waitingRoom.title')}
                            title={t('waitingRoom.title')}
                            aria-expanded={showWaiting}
                            data-cy="header-waiting-chip"
                        >
                            <FontAwesomeIcon icon={faUserClock} />
                            {waitingOnly.length > 0 && (
                                <span className="gs-iconbtn__badge gs-waiting__badge" data-cy="header-waiting-count">
                                    {waitingOnly.length}
                                </span>
                            )}
                        </button>

                        {/* Transient notice under the icon when another session
                            calls a patient. Hidden while the dropdown is open. */}
                        {justCalled && !showWaiting && (
                            <div className="gs-waiting__flash" data-cy="header-waiting-flash">
                                <button
                                    type="button"
                                    className="gs-waiting__flash-main"
                                    onClick={() => { setShowWaiting(true); setJustCalled(null); }}
                                >
                                    <FontAwesomeIcon icon={faBell} className="gs-waiting__flash-icon" />
                                    <span>
                                        <strong>{justCalled.name}</strong> {t('waitingRoom.flash.wasCalled')}
                                        {justCalled.by ? ` ${t('waitingRoom.flash.by', { name: justCalled.by })}` : ''}
                                        {justCalled.at ? ` — ${formatTime(justCalled.at)}` : ''}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    className="gs-waiting__flash-close"
                                    onClick={() => setJustCalled(null)}
                                    aria-label={t('waitingRoom.flash.close')}
                                    data-cy="header-waiting-flash-close"
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            </div>
                        )}

                        {showWaiting && (
                            <div className="gs-notif__dropdown gs-waiting__dropdown">
                                <div className="gs-notif__head">
                                    <span className="gs-notif__title">{t('waitingRoom.title')}</span>
                                </div>

                                {/* Tabs: who's in the room vs who's scheduled today
                                    but hasn't arrived (reception checks them in). */}
                                <div className="gs-waiting__tabs" role="tablist">
                                    <button
                                        type="button"
                                        className={`gs-waiting__tab${waitingTab === 'sala' ? ' gs-waiting__tab--active' : ''}`}
                                        onClick={() => setWaitingTab('sala')}
                                        data-cy="header-waiting-tab-sala"
                                    >
                                        {t('waitingRoom.tabInRoom', { count: waitingOnly.length })}
                                    </button>
                                    <button
                                        type="button"
                                        className={`gs-waiting__tab${waitingTab === 'chegar' ? ' gs-waiting__tab--active' : ''}`}
                                        onClick={() => setWaitingTab('chegar')}
                                        data-cy="header-waiting-tab-chegar"
                                    >
                                        {t('waitingRoom.tabExpected', { count: visibleExpected.length })}
                                    </button>
                                </div>

                                <div className="gs-notif__list">
                                    {waitingTab === 'sala' ? (
                                        <>
                                            {waitingOnly.length === 0 && !lastCalled && (
                                                <div className="gs-notif__empty">{t('waitingRoom.nobodyInRoom')}</div>
                                            )}
                                            {lastCalled && (
                                                <div
                                                    className="gs-notif__item gs-waiting__item gs-waiting__item--called"
                                                    data-cy="header-waiting-called"
                                                >
                                                    <div className="gs-waiting__pos gs-waiting__pos--called" aria-hidden>✓</div>
                                                    <div className="gs-notif__body">
                                                        <div className="gs-notif__row">
                                                            <span className="gs-notif__name">{lastCalled.customerName}</span>
                                                            <span className="gs-notif__time">{formatTime(lastCalled.startTime)}</span>
                                                        </div>
                                                        <div className="gs-waiting__meta">
                                                            {t('waitingRoom.lastCalled')}
                                                            {lastCalled.calledBy ? ` ${t('waitingRoom.calledBy', { name: lastCalled.calledBy })}` : ''}
                                                            {lastCalled.calledAt ? ` — ${formatTime(lastCalled.calledAt)}` : ''}
                                                        </div>
                                                    </div>
                                                    <span className="gs-waiting__called-tag">{t('waitingRoom.calledTag')}</span>
                                                </div>
                                            )}
                                            {waitingOnly.map((appt, idx) => {
                                                const waited = minutesSince(appt.arrivedAt);
                                                const isNext = idx === 0;
                                                return (
                                                    <div
                                                        key={appt.id}
                                                        className={`gs-notif__item gs-waiting__item${isNext ? ' gs-waiting__item--next' : ''}`}
                                                        data-cy="header-waiting-item"
                                                    >
                                                        <div className="gs-waiting__pos" aria-hidden>{idx + 1}</div>
                                                        <div className="gs-notif__body">
                                                            <div className="gs-notif__row">
                                                                <span className="gs-notif__name">
                                                                    {appt.customerName}
                                                                    {isNext && <span className="gs-waiting__next-tag">{t('waitingRoom.next')}</span>}
                                                                </span>
                                                                <span className="gs-notif__time">
                                                                    {formatTime(appt.startTime)}
                                                                </span>
                                                            </div>
                                                            <div className="gs-waiting__meta">
                                                                {waited < 0
                                                                    ? t('waitingRoom.waiting')
                                                                    : waited < 60
                                                                        ? t('waitingRoom.waitingMin', { min: waited })
                                                                        : t('waitingRoom.waitingHourMin', { h: Math.floor(waited / 60), min: waited % 60 })}
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="gs-waiting__call-btn"
                                                            onClick={() => handleCallPatient(appt)}
                                                            data-cy="header-waiting-call"
                                                        >
                                                            {t('waitingRoom.call')}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        <>
                                            {visibleExpected.length === 0 && (
                                                <div className="gs-notif__empty">{t('waitingRoom.nobodyExpected')}</div>
                                            )}
                                            {visibleExpected.map((appt) => (
                                                <div
                                                    key={appt.id}
                                                    className="gs-notif__item gs-waiting__item"
                                                    data-cy="header-expected-item"
                                                >
                                                    <div className="gs-waiting__pos gs-waiting__pos--exp" aria-hidden>
                                                        <FontAwesomeIcon icon={faUserClock} />
                                                    </div>
                                                    <div className="gs-notif__body">
                                                        <div className="gs-notif__row">
                                                            <span className="gs-notif__name">{appt.customerName}</span>
                                                            <span className="gs-notif__time">{formatTime(appt.startTime)}</span>
                                                        </div>
                                                        <div className="gs-waiting__meta">{t('waitingRoom.scheduledToday')}</div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="gs-waiting__checkin-btn"
                                                        onClick={() => handleCheckInExpected(appt)}
                                                        data-cy="header-expected-checkin"
                                                    >
                                                        {t('waitingRoom.checkIn')}
                                                    </button>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    );
                })()}

                {/* Notifications bell + dropdown */}
                <div className="gs-notif" ref={notifRef}>
                    <button
                        type="button"
                        className="gs-iconbtn"
                        onClick={handleBellClick}
                        aria-label={t('header.notif.title')}
                        title={t('header.notif.title')}
                        aria-expanded={showNotifications}
                        data-cy="header-notif-bell"
                    >
                        <FontAwesomeIcon icon={faBell} />
                        {unreadCount > 0 && (
                            <span className="gs-iconbtn__badge" data-cy="header-notif-count">{unreadCount}</span>
                        )}
                    </button>

                    {showNotifications && (
                        <div className="gs-notif__dropdown">
                            <div className="gs-notif__head">
                                <span className="gs-notif__title">{t('header.notif.title')}</span>
                                {unreadCount > 0 && (
                                    <span className="gs-notif__count">{t('header.notif.new', { count: unreadCount })}</span>
                                )}
                            </div>

                            <div className="gs-notif__list">
                                {notifLoading ? (
                                    <div className="gs-notif__empty">{t('header.notif.loading')}</div>
                                ) : notifications.length === 0 ? (
                                    <div className="gs-notif__empty">{t('header.notif.empty')}</div>
                                ) : (
                                    notifications.map((n) => {
                                        const target = notificationTargetMenu(n.category);
                                        return (
                                        <div
                                            key={n.id ?? n.title}
                                            className={`gs-notif__item ${
                                                !n.read ? 'gs-notif__item--unread' : ''
                                            }${target ? ' gs-notif__item--clickable' : ''}`}
                                            data-cy="header-notif-item"
                                            role={target ? 'button' : undefined}
                                            onClick={target ? () => { openNotification(n); setShowNotifications(false); } : undefined}
                                        >
                                            {(() => {
                                                const v = categoryVisual(n.category);
                                                return (
                                                    <div
                                                        className="gs-notif__icon"
                                                        style={{ color: v.color, background: `${v.color}1a` }}
                                                    >
                                                        <FontAwesomeIcon icon={v.icon} />
                                                    </div>
                                                );
                                            })()}
                                            <div className="gs-notif__body">
                                                <div className="gs-notif__row">
                                                    <span className="gs-notif__name">
                                                        {n.title}
                                                    </span>
                                                    <span className="gs-notif__time">
                                                        {relativeTime(n.createdAt, t)}
                                                    </span>
                                                </div>
                                                {n.body && (
                                                    <div className="gs-notif__msg">{n.body}</div>
                                                )}
                                            </div>
                                        </div>
                                        );
                                    })
                                )}
                            </div>

                            <button
                                type="button"
                                className="gs-notif__viewall"
                                onClick={() => { setActiveMenu(MENU.NOTIFICATIONS); setShowNotifications(false); }}
                            >
                                {t('header.notif.viewAll')}
                            </button>
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    className={`gs-iconbtn ${isProfileActive ? 'gs-iconbtn--active' : ''} ${
                        user?.image ? 'gs-iconbtn--avatar' : ''
                    }`}
                    onClick={() => setActiveMenu(MENU.PROFILE)}
                    aria-label={t('header.openProfile')}
                    aria-pressed={isProfileActive}
                    title={t('header.openProfile')}
                >
                    {user?.image ? (
                        <img
                            src={mediaUrl(user.image)}
                            alt="Profile"
                            className="gs-iconbtn__avatar"
                            referrerPolicy="no-referrer"
                        />
                    ) : (
                        <FontAwesomeIcon icon={faUser} />
                    )}
                </button>

                <button
                    type="button"
                    className="gs-logout"
                    onClick={logoutWithRedirect}
                    data-cy="header-logout-btn"
                >
                    {t('header.logout')}
                </button>
            </div>
        </header>
    );
};

export default HeaderComponent;
